import { supabase } from '@/lib/supabase';

import type { ContactKind } from '../lib/contactos';

export interface EmployeeContact {
  id: string;
  user_id: string;
  kind: ContactKind;
  label: string | null;
  value: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmployeeContactInput {
  kind: ContactKind;
  label: string | null;
  value: string;
  is_primary: boolean;
}

// core.employee_contacts no está en database.types.ts (hand-maintained).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const contactsTable = () => (supabase as any).from('employee_contacts');

export async function listContacts(userId: string): Promise<EmployeeContact[]> {
  const { data, error } = await contactsTable()
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as EmployeeContact[];
}

/**
 * Un principal por empleado (índice único parcial). Al marcar uno nuevo, se
 * desmarca antes el anterior: si fuera al revés, el índice rechaza el cambio.
 */
async function soltarPrincipal(userId: string, exceptId?: string) {
  let q = contactsTable().update({ is_primary: false }).eq('user_id', userId).eq('is_primary', true);
  if (exceptId) q = q.neq('id', exceptId);
  const { error } = await q;
  if (error) throw error;
}

export async function createContact(
  userId: string,
  input: EmployeeContactInput,
): Promise<EmployeeContact> {
  if (input.is_primary) await soltarPrincipal(userId);
  const { data, error } = await contactsTable()
    .insert({ ...input, user_id: userId })
    .select('*')
    .single();
  if (error) throw error;
  return data as EmployeeContact;
}

export async function updateContact(
  contact: EmployeeContact,
  input: EmployeeContactInput,
): Promise<EmployeeContact> {
  if (input.is_primary && !contact.is_primary) {
    await soltarPrincipal(contact.user_id, contact.id);
  }
  const { data, error } = await contactsTable()
    .update(input)
    .eq('id', contact.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as EmployeeContact;
}

export async function deleteContact(id: string): Promise<void> {
  const { error } = await contactsTable().delete().eq('id', id);
  if (error) throw error;
}

// ---------- Cuentas vinculadas ----------

export interface LinkedAccounts {
  login: {
    email: string | null;
    created_at: string;
    last_sign_in_at: string | null;
    last_seen_at: string | null;
    sessions: number;
    providers: string[];
  } | null;
  telegram: Array<{
    telegram_id: string;
    username: string | null;
    first_name: string | null;
    linked_at: string;
    last_active_at: string | null;
    active: boolean;
  }>;
  mailboxes: Array<{
    id: string;
    provider: 'gmail' | 'zoho' | 'outlook' | 'smtp_generic';
    email_address: string;
    active: boolean;
    last_used_at: string | null;
    created_at: string;
    has_calendar: boolean;
    has_drive: boolean;
    has_gmail_send: boolean;
    sent: number;
    replied: number;
    bounced: number;
    last_sent_at: string | null;
  }>;
  passkeys: Array<{
    id: string;
    name: string | null;
    device_type: string | null;
    created_at: string;
    last_used_at: string | null;
  }>;
  mfa: Array<{
    factor_type: string;
    name: string | null;
    status: string;
    created_at: string;
  }>;
}

export async function getLinkedAccounts(userId: string): Promise<LinkedAccounts> {
  const { data, error } = await supabase.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'employee_linked_accounts' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { p_user_id: userId } as any,
  );
  if (error) throw error;
  return data as unknown as LinkedAccounts;
}

// ---------- Correos de una casilla ----------

export interface MailboxMessage {
  id: string;
  subject: string | null;
  to_address: string | null;
  status: string;
  sent_at: string | null;
  opened_at: string | null;
  replied_at: string | null;
  bounced_at: string | null;
  scheduled_for: string | null;
  campaign_name: string | null;
}

export interface MailboxMessageDetail extends MailboxMessage {
  body: string | null;
  from_address: string | null;
  bounce_reason: string | null;
  prospect_name: string | null;
}

export async function listMailboxMessages(
  connectionId: string,
): Promise<MailboxMessage[]> {
  const { data, error } = await supabase.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'employee_mailbox_messages' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { p_connection_id: connectionId, p_limit: 100 } as any,
  );
  if (error) throw error;
  return (data ?? []) as unknown as MailboxMessage[];
}

export async function getMailboxMessage(
  emailId: string,
): Promise<MailboxMessageDetail> {
  const { data, error } = await supabase.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'employee_mailbox_message' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { p_email_id: emailId } as any,
  );
  if (error) throw error;
  return data as unknown as MailboxMessageDetail;
}
